import { useMemo, useState } from 'react'
import { Card, Table, Tag, Image, Button, Empty, Modal, Tooltip } from '@arco-design/web-react'
import { IconEye, IconCopy } from '@arco-design/web-react/icon'
import { Message } from '@arco-design/web-react'
import { useRole } from '@/hooks/useRole'
import { useMjLogs, type MjColumnKey } from '@/hooks/useMjLogs'
import MjLogsFilters from '@/components/mj/MjLogsFilters'
import { renderTimestamp, renderUseTime } from '@/utils/render'
import type { MjLogEntry } from '@/api/midjourney'

type MjStatusTone = 'success' | 'failure' | 'in_progress' | 'idle'

const STATUS_META: Record<string, { label: string; tone: MjStatusTone }> = {
  SUCCESS: { label: '成功', tone: 'success' },
  FAILURE: { label: '失败', tone: 'failure' },
  IN_PROGRESS: { label: '进行中', tone: 'in_progress' },
  SUBMITTED: { label: '已提交', tone: 'in_progress' },
  NOT_START: { label: '未启动', tone: 'idle' },
  FAILURE_PAUSED: { label: '已取消', tone: 'failure' },
}

const TONE_COLOR: Record<MjStatusTone, string> = {
  success: 'green',
  failure: 'red',
  in_progress: 'orange',
  idle: 'gray',
}

function renderMjStatus(v?: string) {
  const key = (v || '').toUpperCase()
  const meta = STATUS_META[key] || { label: v || '-', tone: 'idle' as MjStatusTone }
  return (
    <Tag color={TONE_COLOR[meta.tone]} size="small">
      {meta.label}
    </Tag>
  )
}

export default function MidjourneyPage() {
  const { isAdmin } = useRole()
  const {
    filters,
    updateFilters,
    resetFilters,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    items,
    total,
    visibleColumns,
    setVisibleColumns,
  } = useMjLogs(isAdmin)

  const [detailOpen, setDetailOpen] = useState(false)
  const [detailRow, setDetailRow] = useState<MjLogEntry | null>(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null)

  const openDetail = (row: MjLogEntry) => {
    setDetailRow(row)
    setDetailOpen(true)
  }

  const columnsAll = useMemo(() => {
    return {
      submit_time: {
        title: '提交时间',
        dataIndex: 'submit_time',
        width: 170,
        render: (v?: number) => renderTimestamp(v),
      },
      finish_time: {
        title: '完成时间',
        dataIndex: 'finish_time',
        width: 170,
        render: (v?: number) => renderTimestamp(v),
      },
      channel_id: {
        title: '渠道',
        dataIndex: 'channel_id',
        width: 90,
        render: (v?: number) =>
          v ? <Tag size="small">#{v}</Tag> : <span className="text-gray-400">-</span>,
      },
      action: {
        title: '动作',
        dataIndex: 'action',
        width: 120,
        render: (v?: string) =>
          v ? (
            <Tag color="arcoblue" size="small">
              {v}
            </Tag>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      mj_id: {
        title: '任务 ID',
        dataIndex: 'mj_id',
        width: 200,
        render: (v?: string) => (
          <code className="text-xs text-[#374151] break-all">{v || '-'}</code>
        ),
      },
      prompt: {
        title: '提示词',
        dataIndex: 'prompt',
        ellipsis: true,
        render: (v?: string, row?: MjLogEntry) =>
          v ? (
            <Tooltip content="点击查看完整提示词">
              <button
                className="text-sm text-[#2563EB] text-left truncate max-w-full hover:underline"
                onClick={() => row && openDetail(row)}
              >
                {v}
              </button>
            </Tooltip>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      status: {
        title: '状态',
        dataIndex: 'status',
        width: 110,
        render: (v?: string) => renderMjStatus(v),
      },
      progress: {
        title: '进度',
        dataIndex: 'progress',
        width: 100,
        render: (v?: string) =>
          v ? (
            <span className="font-mono text-xs text-[#374151]">{v}</span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      image_url: {
        title: '图片',
        dataIndex: 'image_url',
        width: 110,
        render: (v?: string) =>
          v ? (
            <div
              className="group relative w-16 h-16 rounded-lg overflow-hidden border border-[#E5E7EB] cursor-pointer transition-all hover:shadow-lg"
              onClick={() => setImagePreviewUrl(v)}
            >
              <Image
                src={v}
                width={64}
                height={64}
                preview={false}
                style={{ objectFit: 'cover' }}
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <IconEye className="text-white text-lg" />
              </div>
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
      fail_reason: {
        title: '失败原因',
        dataIndex: 'fail_reason',
        ellipsis: true,
        render: (v?: string) =>
          v ? (
            <span className="text-xs text-[#DC2626]">{v}</span>
          ) : (
            <span className="text-gray-400">-</span>
          ),
      },
    } as const
  }, [])

  const columns = visibleColumns
    .map((key) => (columnsAll as Record<MjColumnKey, unknown>)[key])
    .filter(Boolean) as Array<{ title: string }>

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => Message.success('已复制'))
  }

  return (
    <div className="space-y-4">
      <Card className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <div className="mb-1">
          <div className="text-lg font-semibold text-[#111827]">Midjourney 任务</div>
          <div className="text-xs text-[#9CA3AF]">查看 Midjourney 相关任务记录与产物预览</div>
        </div>
      </Card>

      <Card className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
        <MjLogsFilters
          filters={filters}
          onChange={updateFilters}
          onReset={resetFilters}
          onRefresh={() => query.refetch()}
          isAdmin={isAdmin}
          loading={query.isFetching}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
        />
      </Card>

      <Card
        className="!rounded-2xl !border-[#EAECEF] !shadow-[0_4px_20px_rgba(15,23,42,0.04)]"
        bodyStyle={{ padding: 0 }}
      >
        <Table
          rowKey="id"
          loading={query.isLoading}
          columns={columns as never}
          data={items}
          scroll={{ x: 1400 }}
          stripe={false}
          noDataElement={
            <div className="py-10">
              <Empty description="暂无 Midjourney 记录" />
            </div>
          }
          pagination={{
            current: page,
            pageSize,
            total,
            showTotal: (t) => `共 ${t} 条`,
            sizeCanChange: true,
            pageSizeChangeResetCurrent: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </Card>

      {/* 任务详情 Modal */}
      <Modal
        title="任务详情"
        visible={detailOpen}
        footer={null}
        onCancel={() => setDetailOpen(false)}
        style={{ width: 640 }}
        unmountOnExit
      >
        {detailRow && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-[#9CA3AF]">任务 ID</div>
                <div className="flex items-center gap-2">
                  <code className="text-xs break-all">{detailRow.mj_id || '-'}</code>
                  {detailRow.mj_id && (
                    <Button
                      type="text"
                      size="mini"
                      icon={<IconCopy />}
                      aria-label="复制任务 ID"
                      onClick={() => handleCopy(detailRow.mj_id as string)}
                    />
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#9CA3AF]">状态</div>
                <div>{renderMjStatus(detailRow.status)}</div>
              </div>
              <div>
                <div className="text-xs text-[#9CA3AF]">动作</div>
                <div>{detailRow.action || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-[#9CA3AF]">进度</div>
                <div className="font-mono">{detailRow.progress || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-[#9CA3AF]">提交时间</div>
                <div>{renderTimestamp(detailRow.submit_time)}</div>
              </div>
              <div>
                <div className="text-xs text-[#9CA3AF]">完成时间</div>
                <div>{renderTimestamp(detailRow.finish_time)}</div>
              </div>
              {detailRow.submit_time && detailRow.finish_time && (
                <div>
                  <div className="text-xs text-[#9CA3AF]">耗时</div>
                  <div>{renderUseTime(detailRow.finish_time - detailRow.submit_time)}</div>
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-[#9CA3AF] mb-1">提示词</div>
              <div className="flex items-start gap-2">
                <div className="flex-1 p-3 bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg text-sm whitespace-pre-wrap break-all">
                  {detailRow.prompt || '-'}
                </div>
                {detailRow.prompt && (
                  <Button
                    type="text"
                    size="small"
                    icon={<IconCopy />}
                    aria-label="复制提示词"
                    onClick={() => handleCopy(detailRow.prompt as string)}
                  />
                )}
              </div>
            </div>

            {detailRow.fail_reason && (
              <div>
                <div className="text-xs text-[#9CA3AF] mb-1">失败原因</div>
                <div className="p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#DC2626]">
                  {detailRow.fail_reason}
                </div>
              </div>
            )}

            {detailRow.image_url && (
              <div>
                <div className="text-xs text-[#9CA3AF] mb-1">结果图像</div>
                <div className="rounded-lg overflow-hidden border border-[#E5E7EB]">
                  <Image
                    src={detailRow.image_url}
                    width="100%"
                    style={{ display: 'block' }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 图片预览 Modal */}
      <Modal
        visible={!!imagePreviewUrl}
        footer={null}
        onCancel={() => setImagePreviewUrl(null)}
        title="图片预览"
        style={{ width: 720 }}
        unmountOnExit
      >
        {imagePreviewUrl && (
          <div className="rounded-lg overflow-hidden">
            <Image src={imagePreviewUrl} width="100%" style={{ display: 'block' }} />
          </div>
        )}
      </Modal>
    </div>
  )
}
